import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Input } from '../../ui/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { Search } from 'lucide-react';
import { DTC_STATUS_BITS, DTC_LETTERS, DTC_DIGIT1, DTC_SUBGROUPS, FTB_CATEGORIES, FTB_NAMES, hex2 } from '../../../lib/dtcDecoder/constants';
import { DTC_DATABASE_ROWS } from '../../../lib/dtcDecoder/dtcDatabase';
import { MODE01_PID_ROWS } from '../../../lib/dtcDecoder/obdPids';

const withSearch = (rows) => rows.map((r) => ({ ...r, searchText: (r.searchText || Object.values(r).join(' ')).toLowerCase() }));

const STATUS_ROWS = withSearch(DTC_STATUS_BITS.map((b) => ({ key: b.index, bit: `bit ${b.index}`, mask: `0x${hex2(b.bit)}`, name: b.name, meaning: b.meaning })));

const FTB_ROWS = withSearch([
  ...Object.entries(FTB_CATEGORIES).map(([n, c]) => ({ key: `c${n}`, range: `0x${Number(n).toString(16).toUpperCase()}0–0x${Number(n).toString(16).toUpperCase()}F`, name: c.name, detail: c.detail })),
  { key: 'cA', range: '0xA0–0xEF', name: 'ISO/SAE reserved', detail: 'Not assigned.' },
  { key: 'cF', range: '0xF0–0xFF', name: 'Manufacturer / supplier specific', detail: 'Meaning defined by the OEM or supplier.' },
]);
const FTB_NAME_ROWS = withSearch(Object.entries(FTB_NAMES).map(([v, name]) => ({ key: v, hex: `0x${hex2(Number(v))}`, name })));

const STRUCTURE_ROWS = withSearch(Object.entries(DTC_LETTERS).flatMap(([letter, info]) => [0, 1, 2, 3].map((d) => ({
  key: `${letter}${d}`,
  prefix: `${letter}${d}xxx`,
  bits: `${info.bits} ${d.toString(2).padStart(2, '0')}`,
  system: info.name,
  scope: DTC_DIGIT1[letter][d].label,
  subgroups: DTC_SUBGROUPS[`${letter}${d}`] ? Object.entries(DTC_SUBGROUPS[`${letter}${d}`]).map(([k, v]) => `${letter}${d}${k}xx ${v}`).join('; ') : (letter === 'B' || letter === 'C' ? 'No standard sub-groups' : '—'),
}))));

const Section = ({ title, description, count, children, testId }) => (
  <div className="space-y-2" data-testid={testId}>
    <div>
      <p className="font-chivo uppercase tracking-tight text-sm font-semibold">{title} <span className="font-mono text-xs text-muted-foreground font-normal">({count})</span></p>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
    <div className="rounded-md border border-structure overflow-hidden">{children}</div>
  </div>
);

const Head = ({ cols }) => (
  <TableHeader>
    <TableRow className="bg-muted/50 hover:bg-muted/50">
      {cols.map((c) => <TableHead key={c} className="font-mono text-[11px] uppercase tracking-wider">{c}</TableHead>)}
    </TableRow>
  </TableHeader>
);

const Empty = ({ cols, query }) => (
  <TableRow><TableCell colSpan={cols} className="text-center text-sm text-muted-foreground py-4">No rows match &quot;{query}&quot;.</TableCell></TableRow>
);

const DtcReferenceTables = ({ onPickCode }) => {
  const [query, setQuery] = useState('');
  const filter = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^0x/, '');
    return (rows) => (q ? rows.filter((r) => r.searchText.includes(q)) : rows);
  }, [query]);

  const status = filter(STATUS_ROWS);
  const ftb = filter(FTB_ROWS);
  const ftbNames = filter(FTB_NAME_ROWS);
  const structure = filter(STRUCTURE_ROWS);
  const dtcs = filter(DTC_DATABASE_ROWS);
  const pids = filter(MODE01_PID_ROWS);

  return (
    <Card className="technical-card" data-testid="dtc-reference">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-base">Reference Tables</CardTitle>
        <CardDescription>Status bits, FTB categories, DTC structure, the curated generic DTC list and Mode 01 PID formulas. One search filters every table (try &quot;misfire&quot;, &quot;U01&quot;, &quot;pending&quot;, &quot;0C&quot;).</CardDescription>
        <div className="relative pt-2">
          <Search className="absolute left-2.5 top-[1.15rem] h-4 w-4 text-muted-foreground" />
          <Input className="pl-8 font-mono" placeholder="Search all tables…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search reference tables" data-testid="dtc-reference-search" />
        </div>
      </CardHeader>
      <CardContent className="space-y-8">
        <Section title="DTC status bits (ISO 14229-1)" count={status.length} testId="dtc-ref-status">
          <Table>
            <Head cols={['Bit', 'Mask', 'Name', 'Meaning']} />
            <TableBody>
              {status.map((r) => (
                <TableRow key={r.key}>
                  <TableCell className="font-mono text-xs whitespace-nowrap">{r.bit}</TableCell>
                  <TableCell className="font-mono text-xs">{r.mask}</TableCell>
                  <TableCell className="font-mono text-xs">{r.name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground min-w-[200px]">{r.meaning}</TableCell>
                </TableRow>
              ))}
              {!status.length && <Empty cols={4} query={query} />}
            </TableBody>
          </Table>
        </Section>

        <Section title="Failure Type Byte categories (SAE J2012-DA)" description="The high nibble of the FTB gives its category." count={ftb.length} testId="dtc-ref-ftb">
          <Table>
            <Head cols={['Range', 'Category', 'Covers']} />
            <TableBody>
              {ftb.map((r) => (
                <TableRow key={r.key}>
                  <TableCell className="font-mono text-xs whitespace-nowrap">{r.range}</TableCell>
                  <TableCell className="text-xs font-semibold">{r.name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground min-w-[200px]">{r.detail}</TableCell>
                </TableRow>
              ))}
              {!ftb.length && <Empty cols={3} query={query} />}
            </TableBody>
          </Table>
        </Section>

        <Section title="Named FTB sub-types" description="Only sub-types two independent public J2012-DA transcriptions agree on are named; every other FTB is shown by category only." count={ftbNames.length} testId="dtc-ref-ftb-names">
          <Table>
            <Head cols={['FTB', 'Sub-type']} />
            <TableBody>
              {ftbNames.map((r) => (
                <TableRow key={r.key}>
                  <TableCell className="font-mono text-xs">{r.hex}</TableCell>
                  <TableCell className="text-xs">{r.name}</TableCell>
                </TableRow>
              ))}
              {!ftbNames.length && <Empty cols={2} query={query} />}
            </TableBody>
          </Table>
        </Section>

        <Section title="DTC letter / digit structure (SAE J2012)" count={structure.length} testId="dtc-ref-structure">
          <Table>
            <Head cols={['Prefix', 'Bits 7–4', 'System', 'Code type', 'Sub-groups (3rd character)']} />
            <TableBody>
              {structure.map((r) => (
                <TableRow key={r.key}>
                  <TableCell className="font-mono text-xs font-semibold">{r.prefix}</TableCell>
                  <TableCell className="font-mono text-xs whitespace-nowrap">{r.bits}</TableCell>
                  <TableCell className="text-xs">{r.system}</TableCell>
                  <TableCell className="text-xs min-w-[140px]">{r.scope}</TableCell>
                  <TableCell className="text-[11px] text-muted-foreground min-w-[260px]">{r.subgroups}</TableCell>
                </TableRow>
              ))}
              {!structure.length && <Empty cols={5} query={query} />}
            </TableBody>
          </Table>
        </Section>

        <Section title="Curated generic DTCs" description="Common ISO/SAE-controlled codes, described in our own words (not the copyrighted SAE J2012 text). Tap a row to decode it." count={dtcs.length} testId="dtc-ref-dtcs">
          <Table>
            <Head cols={['Code', 'Description', 'Typical causes']} />
            <TableBody>
              {dtcs.map((r) => (
                <TableRow key={r.code} className={onPickCode ? 'cursor-pointer' : ''} onClick={() => onPickCode && onPickCode(r.code)} data-testid={`dtc-ref-row-${r.code}`}>
                  <TableCell className="font-mono text-xs font-semibold">{r.code}</TableCell>
                  <TableCell className="text-xs min-w-[200px]">{r.description}</TableCell>
                  <TableCell className="text-xs text-muted-foreground min-w-[200px]">{r.causes}</TableCell>
                </TableRow>
              ))}
              {!dtcs.length && <Empty cols={3} query={query} />}
            </TableBody>
          </Table>
        </Section>

        <Section title="OBD-II Mode 01 PID formulas (SAE J1979)" count={pids.length} testId="dtc-ref-pids">
          <Table>
            <Head cols={['PID', 'Name', 'Bytes', 'Formula / encoding']} />
            <TableBody>
              {pids.map((r) => (
                <TableRow key={r.pid}>
                  <TableCell className="font-mono text-xs font-semibold">{r.hex}</TableCell>
                  <TableCell className="text-xs min-w-[160px]">{r.name}</TableCell>
                  <TableCell className="font-mono text-xs">{r.bytes}</TableCell>
                  <TableCell className="font-mono text-xs min-w-[160px]">{r.formulaText}</TableCell>
                </TableRow>
              ))}
              {!pids.length && <Empty cols={4} query={query} />}
            </TableBody>
          </Table>
        </Section>
      </CardContent>
    </Card>
  );
};

export default DtcReferenceTables;
